/**
 * A map popup that never moves the map. Unlike InfoWindow there is no auto-pan:
 * one shell element lives in the map's floatPane (a google.maps.OverlayView) and
 * is placed beside its anchor with `placePopup` in map-container pixels, clear of
 * the map's own controls. It follows the map on every draw (rigidly once its
 * anchor has left the map, so it leaves with its marker) and re-places when its
 * content resizes. The caller renders the content into `shell` and owns hover,
 * pin and focus behaviour.
 */
import { followPopup, type PixelBox, type PixelSize, type PlacementOptions, type PopupFollow, type PopupSide } from './mapPopupPlacement'

export type PopupMode = 'preview' | 'pinned'
/** `element` is what the popup points at; `root` is the marker content holding it, anchored bottom-centre on `position`. */
export interface PopupAnchor { element: HTMLElement; root: HTMLElement; position: google.maps.LatLng | google.maps.LatLngLiteral }
/** `avoid`: extra boxes to keep clear of (container px), on top of the visible Google controls found in the map. */
export interface MapPopupOptions {
  id: string; className: string; placement?: Omit<PlacementOptions, 'prefer' | 'avoid'>
  avoid?: (container: PixelSize) => readonly PixelBox[]
}
/** `instant`: a swap between visible popups (no entrance animation); `prefer`: side tried first while it fits. */
export interface OpenOptions { instant?: boolean; prefer?: PopupSide }

/**
 * The anchor's box relative to its marker position pixel. Both rectangles come
 * from the same frame, so a marker the map has not positioned yet (just after
 * re-clustering) still yields the right offset.
 */
function offsetOf(anchor: PopupAnchor): PixelBox | null {
  const box = anchor.element.getBoundingClientRect()
  const root = anchor.root.getBoundingClientRect()
  if (box.width <= 0 || box.height <= 0 || root.width <= 0) return null
  return { x: box.left - (root.left + root.width / 2), y: box.top - root.bottom, width: box.width, height: box.height }
}

/** Google's controls draw above the floatPane: fullscreen, zoom/camera and the attribution links. */
const CONTROLS = '.gm-fullscreen-control, .gm-bundled-control, .gm-style-cc'

/** The visible controls' boxes in container pixels. */
function controlBoxes(mapDiv: HTMLElement): PixelBox[] {
  const frame = mapDiv.getBoundingClientRect()
  const left = frame.left + mapDiv.clientLeft
  const top = frame.top + mapDiv.clientTop
  const shown = (box: DOMRect) => box.width > 0 && box.height > 0
  const boxes: PixelBox[] = []
  for (const control of mapDiv.querySelectorAll(CONTROLS)) {
    // The zoom/camera wrapper measures 0x0 around absolutely positioned parts: join its visible parts instead.
    const own = control.getBoundingClientRect()
    const parts = shown(own) ? [own] : [...control.querySelectorAll('*')].map(part => part.getBoundingClientRect()).filter(shown)
    if (parts.length === 0) continue
    const x0 = Math.min(...parts.map(box => box.left)), y0 = Math.min(...parts.map(box => box.top))
    const x1 = Math.max(...parts.map(box => box.right)), y1 = Math.max(...parts.map(box => box.bottom))
    boxes.push({ x: x0 - left, y: y0 - top, width: x1 - x0, height: y1 - y0 })
  }
  return boxes
}

export class MapPopupOverlay {
  /** Hidden while closed (a hidden shell never blocks map hits); unplaced until measured (no `data-side`). */
  readonly shell: HTMLDivElement
  private readonly map: google.maps.Map
  private readonly view: google.maps.OverlayView
  private readonly placement: Omit<PlacementOptions, 'prefer' | 'avoid'>
  private readonly avoid: ((container: PixelSize) => readonly PixelBox[]) | undefined
  private readonly resize: ResizeObserver
  private anchor: PopupAnchor | null = null
  private offset: PixelBox | null = null
  private side: PopupSide | undefined = undefined
  /** The latest placement made while the anchor was inside the map; an off-map anchor keeps its offset. */
  private last: PopupFollow | null = null

  constructor(maps: google.maps.MapsLibrary, map: google.maps.Map, options: MapPopupOptions) {
    const shell = document.createElement('div')
    shell.className = options.className
    shell.id = options.id
    shell.hidden = true
    maps.OverlayView.preventMapHitsAndGesturesFrom(shell)
    this.shell = shell
    this.map = map
    this.placement = options.placement ?? {}
    this.avoid = options.avoid
    this.view = new maps.OverlayView()
    this.view.onAdd = () => { this.view.getPanes()?.floatPane.append(shell) }
    this.view.draw = () => this.place()
    this.view.onRemove = () => { shell.remove() }
    this.view.setMap(map)
    this.resize = new ResizeObserver(() => this.place())
    this.resize.observe(shell)
  }

  isOpen(): boolean {
    return !this.shell.hidden
  }

  /** Show for `anchor`; the caller renders the content, then calls `place()` (a resize also places). */
  open(anchor: PopupAnchor, mode: PopupMode, options: OpenOptions = {}): void {
    const shell = this.shell
    // A new anchor has no offset to keep; the same anchor (a preview being pinned) keeps it.
    if (shell.hidden || anchor.element !== this.anchor?.element) this.last = null
    if (shell.hidden) {
      delete shell.dataset.side
      delete shell.dataset.instant
      this.side = options.prefer
      shell.hidden = false
    } else {
      // Kept until close: removing it while open would replay the entrance animation.
      if (options.instant) shell.dataset.instant = ''
      if (options.prefer) this.side = options.prefer
    }
    this.setMode(mode)
    this.attach(anchor)
  }

  /** Point at a new element (re-clustering) without changing the content or mode. */
  reanchor(anchor: PopupAnchor): void {
    this.last = null
    this.attach(anchor)
    this.place()
  }

  /** A preview is a tooltip for its pin; a pinned card's content carries its own dialog role. */
  setMode(mode: PopupMode): void {
    this.shell.dataset.mode = mode
    if (mode === 'preview') this.shell.setAttribute('role', 'tooltip')
    else this.shell.removeAttribute('role')
  }

  close(): void {
    const shell = this.shell
    shell.hidden = true
    delete shell.dataset.side
    delete shell.dataset.instant
    delete shell.dataset.clipped
    shell.removeAttribute('role')
    this.anchor = null
    this.offset = null
    this.last = null
  }

  /** Measure the shell and write data-side, data-clipped and --x/--y/--arrow (floatPane px). */
  place(): void {
    const { shell, anchor, offset } = this
    if (shell.hidden || !anchor || !offset) return
    // Undefined until the overlay has been added to the map.
    const projection = this.view.getProjection() as google.maps.MapCanvasProjection | undefined
    const at = projection?.fromLatLngToContainerPixel(anchor.position)
    const pane = projection?.fromLatLngToDivPixel(anchor.position)
    if (!at || !pane) return
    const mapDiv = this.map.getDiv()
    const container = { width: mapDiv.clientWidth, height: mapDiv.clientHeight }
    const followed = followPopup(
      { x: at.x + offset.x, y: at.y + offset.y, width: offset.width, height: offset.height },
      container, this.last,
      { width: shell.offsetWidth, height: shell.offsetHeight },
      { ...this.placement, prefer: this.side, avoid: [...controlBoxes(mapDiv), ...(this.avoid?.(container) ?? [])] },
    )
    // Off the map the card keeps this offset, so it moves with its marker and the map div clips it.
    if (followed.inside) this.last = followed
    const result = followed.placement
    this.side = result.side
    shell.style.setProperty('--x', `${result.x + pane.x - at.x}px`)
    shell.style.setProperty('--y', `${result.y + pane.y - at.y}px`)
    shell.style.setProperty('--arrow', `${result.arrow}px`)
    if (result.clipped) shell.dataset.clipped = ''
    else delete shell.dataset.clipped
    shell.dataset.side = result.side
  }

  dispose(): void {
    this.resize.disconnect()
    this.view.setMap(null)
    this.shell.remove()
    this.anchor = null
    this.offset = null
  }

  /** An unmeasurable anchor keeps the previous offset, or points at the position itself. */
  private attach(anchor: PopupAnchor): void {
    this.anchor = anchor
    this.offset = offsetOf(anchor) ?? this.offset ?? { x: 0, y: 0, width: 0, height: 0 }
  }
}
